import React, { useState, useRef } from 'react';
import { useFetching } from 'components/hooks/useFetching';
import { NotificationManager } from 'react-notifications';
import Strings from 'values/Strings';
import FileUploadService from 'services/FileUploadService';
import env from '../../../../../package.json';
import 'styles/css/logo-upload.css';

const LogoUpload = ({ onLogoUploaded, currentLogo, eventId = 'default' }) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(currentLogo);
    const fileInputRef = useRef(null);

    // Загрузка логотипа
    const [uploadLogo, loadingUpload] = useFetching(async (file) => {
        try {
            const result = await FileUploadService.uploadLogo(file, eventId);

            console.log('env.nodeHost :', env.nodeHost);
            console.log('result.filePath :', result.filePath);
            // Создаем полный URL для отображения
            const fullUrl = `${env.nodeHost}${result.filePath}`;
            setPreviewUrl(fullUrl);

            // Уведомляем родительский компонент
            onLogoUploaded(fullUrl);

            NotificationManager.success('Логотип успешно загружен', Strings.notificationsSuccessTitle, 3000);
            return result;
        } catch (error) {
            console.error('Ошибка загрузки логотипа:', error);
            NotificationManager.error('Ошибка загрузки логотипа', Strings.notificationsErrorTitle, 3000);
            throw error;
        }
    }, false);

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);

        const files = e.dataTransfer.files;
        if (files.length > 0) {
            handleFileSelect(files[0]);
        }
    };

    const handleFileSelect = (file) => {
        // Проверяем тип файла
        if (!file.type.startsWith('image/')) {
            NotificationManager.warning('Пожалуйста, выберите изображение', Strings.notificationsWarningTitle, 3000);
            return;
        }

        // Проверяем размер файла (5MB)
        if (file.size > 5 * 1024 * 1024) {
            NotificationManager.warning('Размер файла не должен превышать 5MB', Strings.notificationsWarningTitle, 3000);
            return;
        }

        // Создаем предварительный просмотр
        const reader = new FileReader();
        reader.onload = (e) => {
            setPreviewUrl(e.target.result);
        };
        reader.readAsDataURL(file);

        // Загружаем файл
        setIsUploading(true);
        uploadLogo(file).finally(() => {
            setIsUploading(false);
        });
    };

    const handleFileInputChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    const handleRemove = () => {
        setPreviewUrl(null);
        onLogoUploaded(null);
    };

    return (
        <div className="logo-upload-container">
            <div
                className={`logo-upload-area ${isDragOver ? 'drag-over' : ''} ${isUploading ? 'uploading' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleClick}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileInputChange}
                    style={{ display: 'none' }}
                />

                {previewUrl ? (
                    <div className="logo-preview">
                        <img src={previewUrl} alt="Предварительный просмотр логотипа" />
                        <div className="logo-actions">
                            <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleClick();
                                }}
                                disabled={isUploading}
                            >
                                Заменить
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemove();
                                }}
                                disabled={isUploading}
                            >
                                Удалить
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="logo-upload-placeholder">
                        {isUploading ? (
                            <div className="upload-spinner">
                                <div className="spinner"></div>
                                <p>Загрузка...</p>
                            </div>
                        ) : (
                            <>
                                <div className="upload-icon">📁</div>
                                <p>Перетащите логотип сюда или нажмите для выбора</p>
                                <p className="upload-hint">Поддерживаются: JPG, PNG, GIF (до 5MB)</p>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default LogoUpload;
